import { Component, Input, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgentMessage, AgentMessageAttachment } from '../../models/agent-message.model';

@Component({
  selector: 'app-agent-conversation-transcript',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex-1 min-h-0 overflow-y-auto p-4 space-y-4" #scrollMe>
      <div *ngIf="messages.length === 0" class="text-center text-gray-500 py-10">
        No hay mensajes en esta conversación.
      </div>

      <div *ngFor="let msg of messages" class="flex flex-col"
           [ngClass]="{
             'items-start': msg.participantType === 'VISITOR' || msg.participantType === 'SYSTEM',
             'items-end': msg.participantType === 'AGENT' || msg.participantType === 'BOT'
           }">

        <!-- System Message -->
        <div *ngIf="msg.participantType === 'SYSTEM'" class="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full my-2 self-center text-center max-w-[80%]">
          {{ msg.contentText }}
          <span class="ml-1 opacity-70">{{ msg.sentAt | date:'shortTime' }}</span>
        </div>

        <!-- Chat Bubble (Visitor) -->
        <div *ngIf="msg.participantType === 'VISITOR'" class="flex flex-col items-start max-w-[80%]">
          <span class="text-[10px] text-gray-500 mb-0.5 ml-1">{{ msg.senderName || 'Visitante' }}</span>
          <div class="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 px-4 py-2 rounded-2xl rounded-tl-none shadow-sm">
            <p *ngIf="msg.contentText" class="text-sm break-words whitespace-pre-wrap">{{ msg.contentText }}</p>
            <ng-container *ngTemplateOutlet="adjunto; context: { $implicit: adjuntoDe(msg), oscuro: false }"></ng-container>
            <div class="text-[10px] text-gray-400 mt-1 text-right">{{ msg.sentAt | date:'shortTime' }}</div>
          </div>
        </div>

        <!-- Chat Bubble (Agent/Bot) -->
        <div *ngIf="msg.participantType === 'AGENT' || msg.participantType === 'BOT'" class="flex flex-col items-end max-w-[80%]">
          <div class="flex items-center mb-0.5 mr-1 gap-1">
            <span *ngIf="msg.participantType === 'BOT'" class="bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400 text-[9px] font-bold px-1.5 py-0.5 rounded">BOT</span>
            <span *ngIf="msg.participantType === 'AGENT'" class="bg-brand-50 text-brand-600 dark:bg-brand-500/20 dark:text-brand-300 text-[9px] font-bold px-1.5 py-0.5 rounded">AGENTE</span>
            <span class="text-[10px] text-gray-500">{{ msg.senderName || (msg.participantType === 'BOT' ? 'Sistema' : 'Tú') }}</span>
          </div>
          <!-- inc63: la burbuja del agente usaba la clase bg-primary, que NO existe en el
               tema (los tokens son brand/success/error/warning). Sin fondo real, el texto
               blanco quedaba prácticamente invisible sobre el lienzo claro del chat.
               Agente = brand (azul); Bot = índigo, para que sigan distinguiéndose entre sí. -->
          <div class="px-4 py-2 rounded-2xl rounded-tr-none shadow-sm text-white"
               [ngClass]="msg.participantType === 'BOT' ? 'bg-indigo-500' : 'bg-brand-500'">
            <p *ngIf="msg.contentText" class="text-sm break-words whitespace-pre-wrap">{{ msg.contentText }}</p>
            <ng-container *ngTemplateOutlet="adjunto; context: { $implicit: adjuntoDe(msg), oscuro: true }"></ng-container>
            <div class="text-[10px] text-white/70 mt-1 text-right flex items-center justify-end gap-1">
              {{ msg.sentAt | date:'shortTime' }}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Adjunto del mensaje (inc71). Las imágenes se ven en línea; el resto es un enlace
         de descarga con nombre y peso. -->
    <ng-template #adjunto let-file let-oscuro="oscuro">
      <ng-container *ngIf="file">
        <a *ngIf="esImagen(file); else archivo" [href]="file.url" target="_blank" rel="noopener noreferrer"
           class="mt-1.5 block overflow-hidden rounded-xl border"
           [ngClass]="oscuro ? 'border-white/30' : 'border-gray-200 dark:border-gray-700'">
          <img [src]="file.url" [alt]="file.name" class="block max-h-56 w-full object-cover" loading="lazy" />
        </a>
        <ng-template #archivo>
          <a [href]="file.url" target="_blank" rel="noopener noreferrer"
             class="mt-1.5 flex items-center gap-2 rounded-xl border px-3 py-2 transition"
             [ngClass]="oscuro
               ? 'border-white/30 bg-white/10 hover:bg-white/20 text-white'
               : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 text-gray-700 dark:text-gray-200'">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            <span class="min-w-0 flex-1 truncate text-xs font-medium">{{ file.name }}</span>
            <span class="shrink-0 text-[10px] opacity-70">{{ tamanoLegible(file.size) }}</span>
          </a>
        </ng-template>
      </ng-container>
    </ng-template>
  `
})
export class AgentConversationTranscriptComponent implements AfterViewChecked {
  @Input() messages: AgentMessage[] = [];
  @ViewChild('scrollMe') private myScrollContainer!: ElementRef;

  private prevMsgCount = 0;

  ngAfterViewChecked() {
    if (this.messages.length > this.prevMsgCount) {
      this.scrollToBottom();
      this.prevMsgCount = this.messages.length;
    }
  }

  /**
   * Adjunto declarado en `contentJson.attachment`, o null.
   *
   * Solo se acepta http(s) absoluta: el mismo criterio que el widget, para que un
   * `javascript:` guardado en el JSON no llegue nunca al DOM.
   */
  adjuntoDe(message: AgentMessage): AgentMessageAttachment | null {
    if (!message.contentJson) return null;
    try {
      const parsed = JSON.parse(message.contentJson);
      const file = parsed?.attachment;
      if (!file || typeof file.url !== 'string' || !/^https?:\/\//i.test(file.url)) return null;
      return { url: file.url, name: file.name || 'archivo', contentType: file.contentType, size: file.size };
    } catch {
      return null;
    }
  }

  esImagen(file: AgentMessageAttachment): boolean {
    return (file.contentType || '').startsWith('image/');
  }

  tamanoLegible(bytes: number | undefined): string {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private scrollToBottom(): void {
    try {
      this.myScrollContainer.nativeElement.scrollTop = this.myScrollContainer.nativeElement.scrollHeight;
    } catch(err) { }
  }
}
